import React, {useState, useRef, useEffect} from "react";
import {
    Paper, Typography, Button, Modal, Box, Grid, Dialog, CircularProgress,
    DialogTitle, DialogContent, DialogActions, Stack, Chip, Alert, Divider, IconButton
} from "@mui/material";
import {
    GetEntraIdTenancyUrl, GetConflictingEntraIdUsers, GetTenancyStatus,
    DisconnectEntraIdTenancy
} from "../api/endpoints";
import {UploadExcel} from "../api/endpoints";
import {toastErrorNotify} from "../helpers/toastNotify";
import {successNote} from "../helpers/toastNotify";
import "../index.css";
import apiClient from "../api/apiClient";
import Header from "../layout/Header";
import EntraIdGraphicCard from "../components/EmployeeComponents/EntraIdGraphicCard";
import EntraIdConflictingUsersCard from "../components/EmployeeComponents/EntraIdConflictingUsersCard";
import DialogComponent from "../components/common/DialogComponent";

const POPUP_PENDING_KEY = "entra_popup_pending";

const IntegrasjonPage = () => {
    const [license, setLicense] = useState("");
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const [isTenantConnected, setIsTenantConnected] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [confirmEntraIdOpen, setConfirmEntraIdOpen] = useState(false);
    const [confirmUploadExcelOpen, setConfirmUploadExcelOpen] = useState(false);
    const [confirmDisconnectOpen, setConfirmDisconnectOpen] = useState(false);
    const [tenancyRequestUrl, setTenancyRequestUrl] = useState("");

    const [entraIdLoading, setEntraIdLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [disconnectLoading, setDisconnectLoading] = useState(false);

    // null when no rejected rows to show; { conflictedUsers, alreadyClaimedUsers } otherwise
    const [conflictReport, setConflictReport] = useState(null);

    // Persistent admin-instruction dialog. Set to a non-empty string to show it.
    // Triggered after a successful disconnect or after an Excel upload that implicitly
    // disconnected an existing Entra ID tenancy. The text is supplied by the backend so
    // wording stays consistent across entry points.
    const [adminInstruction, setAdminInstruction] = useState(null);

    const employeeImage = "https://res.cloudinary.com/making-view-as/image/upload/v1763559444/employeeproperties.jpg";

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    useEffect(() => {
        const fetchTenancyStatus = async () => {
            try {
                setLoading(true);
                setError(null);

                const response = await apiClient.get(GetEntraIdTenancyUrl);
                const {requestUrl, hasExcelUsers} = response.data.data;
                setIsTenantConnected(!hasExcelUsers);
            } catch (err) {
                console.log(err);
                if (err.response) {
                    setError(`${err.response.status} ${err.response.data?.message || ""}`);
                } else if (err.request) {
                    setError("Server did not respond. Please try again later.");
                } else {
                    setError(err.message || "Unknown error");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchTenancyStatus();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get("entra_status");

        if (status) {
            if (window.opener) {
                try {
                    window.opener.postMessage({
                        type: "entra_callback",
                        status,
                        message: params.get("message")
                    }, window.location.origin);
                } catch (e) {
                    sessionStorage.removeItem(POPUP_PENDING_KEY);
                    if (status === "success") {
                        successNote("Entra ID tilkobling vellykket!");
                        setIsTenantConnected(true);
                    } else {
                        toastErrorNotify("Tilkobling feilet: " + (params.get("message") ?? "ukjent feil"));
                    }
                }
                window.close();
            } else {
                sessionStorage.removeItem(POPUP_PENDING_KEY);
                if (status === "success") {
                    successNote("Entra ID tilkobling vellykket!");
                    setIsTenantConnected(true);
                } else {
                    toastErrorNotify("Tilkobling feilet: " + (params.get("message") ?? "ukjent feil"));
                }
                window.history.replaceState({}, "", "/integrasjon");
            }
            return;
        }

        if (sessionStorage.getItem(POPUP_PENDING_KEY) === "true") {
            setEntraIdLoading(true);
            const existingPopup = window.open("", "entra_popup");
            attachPopupListeners(existingPopup);
        }
    }, []);

    const startEntraIdPopup = (targetUrl) => {
        setEntraIdLoading(true);
        sessionStorage.setItem(POPUP_PENDING_KEY, "true");

        const popup = window.open(targetUrl, "entra_popup", "width=600,height=700");
        attachPopupListeners(popup);
    };

    const attachPopupListeners = (popup) => {
        if (!popup) {
            sessionStorage.removeItem(POPUP_PENDING_KEY);
            setEntraIdLoading(false);
            return;
        }

        let callbackReceived = false;

        const cleanup = () => {
            clearInterval(pollClosed);
            clearTimeout(timeout);
            window.removeEventListener("message", handleMessage);
            sessionStorage.removeItem(POPUP_PENDING_KEY);
            setEntraIdLoading(false);
        };

        const handleMessage = (event) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== "entra_callback") return;
            callbackReceived = true;
            cleanup();
            if (event.data.status === "success") {
                successNote("Entra ID tilkobling vellykket!");
                setIsTenantConnected(true);
            } else {
                toastErrorNotify("Tilkobling feilet: " + (event.data.message ?? "ukjent feil"));
            }
        };

        const pollClosed = setInterval(() => {
            if (popup.closed) {
                cleanup();
                if (!callbackReceived)
                    toastErrorNotify("Tilkoblingen ble avbrutt.");
            }
        }, 500);

        const timeout = setTimeout(() => {
            cleanup();
        }, 5 * 60 * 1000);

        window.addEventListener("message", handleMessage);
    };

    const handleEstablishTenancy = async (e) => {
        e.preventDefault();
        try {
            const response = await apiClient.get(GetEntraIdTenancyUrl);

            if (response.status != 200) {
                toastErrorNotify("Failed to retrieve url for requesting tenancy.\nError message: " + response.data?.message);
                return;
            }

            const {hasExcelUsers, requestUrl} = response?.data?.data ?? {};

            setTenancyRequestUrl(requestUrl);
            if (hasExcelUsers) {
                setConfirmEntraIdOpen(true);
            } else {
                handleConfirmEntraId(requestUrl);
            }
        } catch (err) {
            console.log(err);
            if (err.response) {
                setError(`${err.response.status} ${err.response.data?.message || ""}`);
            } else if (err.request) {
                setError("Server did not respond. Please try again later.");
            } else {
                setError(err.message || "Unknown error");
            }
        }
    };

    const handleConfirmEntraId = (requestUrl) => {
        setConfirmEntraIdOpen(false);
        startEntraIdPopup(requestUrl ?? tenancyRequestUrl);
    };

    const handleCancelEntraId = () => {
        setConfirmEntraIdOpen(false);
    };

    // ── Disconnect flow ─────────────────────────────────────────────────────
    // The backend can clean up our side (DB, KeyVault, disabled users) but cannot
    // remove the Enterprise Application from the customer's Entra tenant — a service
    // principal cannot delete or disable itself via Microsoft Graph. So after a
    // successful disconnect we surface the admin-instruction message returned by the
    // backend (Norwegian), telling the admin to remove "Dr. Cloud" from
    // Azure Portal → Enterprise Applications themselves.
    const handleDisconnectTenancy = () => {
        setConfirmDisconnectOpen(true);
    };

    const handleCancelDisconnect = () => {
        setConfirmDisconnectOpen(false);
    };

    const handleConfirmDisconnect = async () => {
        setConfirmDisconnectOpen(false);
        setDisconnectLoading(true);
        try {
            const response = await apiClient.post(DisconnectEntraIdTenancy);

            if (response?.data?.success) {
                setIsTenantConnected(false);
                successNote("Entra ID-tilkoblingen er fjernet.");
                const message = response?.data?.data;
                if (message)
                    setAdminInstruction(message);
            } else {
                toastErrorNotify(response?.data?.message || "Klarte ikke å koble fra Entra ID.");
            }
        } catch (err) {
            toastErrorNotify(err.response?.data?.message || "Klarte ikke å koble fra Entra ID. Vennligst prøv igjen.");
        } finally {
            setDisconnectLoading(false);
        }
    };

    const handleConfirmUploadExcel = async () => {
        setConfirmUploadExcelOpen(false);
        setExcelLoading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);

            const response = await apiClient.post(UploadExcel, formData, {
                headers: {"Content-Type": "multipart/form-data"},
            });

            const data = response?.data?.data ?? {};
            const conflictedUsers = data.conflictedUsers ?? [];
            const alreadyClaimedUsers = data.alreadyClaimedUsers ?? [];
            const invalidRowMessages = data.invalidRowMessages ?? [];
            const entraDisconnectMessage = data.entraDisconnectMessage ?? null;
            const hasRejections = conflictedUsers.length > 0 || alreadyClaimedUsers.length > 0 || invalidRowMessages.length > 0;

            setFile(null);

            // Excel upload always replaces an existing Entra ID integration if one is present.
            // If the backend tells us a disconnect actually happened, reflect that in our local
            // connection state and surface the admin-instruction message in a persistent dialog.
            if (entraDisconnectMessage) {
                setIsTenantConnected(false);
                setAdminInstruction(entraDisconnectMessage);
            }

            if (hasRejections) {
                setConflictReport({conflictedUsers, alreadyClaimedUsers, invalidRowMessages});
            } else {
                successNote("Filen er lastet opp!");
            }
        } catch (err) {
            toastErrorNotify(err.response?.data?.message || "Klarte ikke å laste opp filen. Vennligst verifiser innholdet og prøv igjen.");
        } finally {
            setExcelLoading(false);
        }
    };

    const handleCancelUploadExcel = () => {
        setConfirmUploadExcelOpen(false);
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
        }
    };

    const handleFileUpload = async () => {
        if (!file) {
            toastErrorNotify("Vennligst velg en excel fil.");
            return;
        }
        try {
            const response = await apiClient.get(GetTenancyStatus);
            if (response.status != 200) {
                toastErrorNotify("Failed to retrieve tenancy status.\nError message: " + response.data?.message);
                return;
            }

            let hasTenancy = response?.data;

            if (hasTenancy) {
                setConfirmUploadExcelOpen(true);
            } else
                await handleConfirmUploadExcel();
        } catch (err) {
            toastErrorNotify(err.response?.data?.message || "Vennligst prøv igjen.");
        }
    };

    return (
        <main>
            <Header header={'Integrasjon'} headerSize={'h6'} className={'integrasjon'}/>

            <DialogComponent
                open={confirmEntraIdOpen}
                header="Eksisterende Excel-brukere oppdaget"
                body="Vi fant brukere som er importert fra Excel. Å fortsette vil erstatte dem med brukere fra Entra ID, men vi vil forsøke å slå sammen informasjonen fra Excel med Entra ID slik at minst mulig data forsvinner. Ønsker du å fortsette med integreringen?"
                onConfirm={() => handleConfirmEntraId(tenancyRequestUrl)}
                onCancel={handleCancelEntraId}
            />
            <DialogComponent
                open={confirmUploadExcelOpen}
                header="Eksisterende Entra ID integrasjon oppdaget"
                body="Vi ser dere allerede er integrert med Entra ID. Å fortsette vil fjerne integrasjonen og all lagret informasjon om brukere fra Entra ID. Ønsker du å gå videre med Excel-opplastingen?"
                onConfirm={handleConfirmUploadExcel}
                onCancel={handleCancelUploadExcel}
            />
            <DialogComponent
                open={confirmDisconnectOpen}
                header="Koble fra Entra ID?"
                body={'Dette fjerner tilkoblingen mellom Dr. Cloud og Entra ID-tenanten deres, og deaktiverer alle brukere som ble synkronisert fra Entra ID. Etter frakoblingen må en administrator hos dere også fjerne applikasjonen "Dr. Cloud" fra Azure Portal for å fullføre frakoblingen — vi viser nøyaktige steg etter at frakoblingen er gjennomført. Ønsker du å fortsette?'}
                onConfirm={handleConfirmDisconnect}
                onCancel={handleCancelDisconnect}
            />

            <ConflictReportDialog
                report={conflictReport}
                onClose={() => setConflictReport(null)}
            />

            <AdminInstructionDialog
                message={adminInstruction}
                onClose={() => setAdminInstruction(null)}
            />

            <Grid container maxWidth="md" rowSpacing={3} columnSpacing={{xs: 1, sm: 2, md: 3}} sx={{mt: 1}}>
                <Paper sx={{m: 4, p: 3}}>
                    <Grid item xs={12}>
                        <Typography variant="h5" align="center" sx={{mb: 6}} className="container-header">
                            Importer brukere fra Entra ID
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        {loading ? (
                            <Typography variant="body1">
                                Henter Entra ID status for din bedrift.
                            </Typography>
                        ) : !isTenantConnected ? (
                            <Typography variant="body1">
                                Entra ID gjør det enkelt for dere å administrere ansatte og informasjonen om dem, og
                                dere kan selv definere hvilke brukere som skal bli tilgjengelige her på Dr. Cloud.
                                <br/><br/>Det er enkelt å motta programmet vårt på Entra ID!
                                <br/>Trykk på "Start tilkobling" og følg stegene.
                            </Typography>
                        ) : (
                            <Typography variant="body1">
                                Tilkobling allerede opprettet. Vi henter oppdatert informasjon om ansatte ca hvert 30. minutt.
                                <br/> Det er bare å trykke på knappen under for å gjennomføre integreringen på nytt.
                                Dette kan for eksempel være nyttig dersom tillatelser har endret seg.
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12} sx={{mt: 3}}>
                        <Grid container justifyContent="space-between" alignItems="center" rowSpacing={1}>
                            <Button
                                variant="contained"
                                className="PrimaryButton"
                                sx={{height: 40, width: 200}}
                                onClick={handleEstablishTenancy}
                                disabled={entraIdLoading || excelLoading || disconnectLoading}
                                startIcon={(entraIdLoading) ? <CircularProgress size={16} color="inherit"/> : null}
                            >
                                {isTenantConnected ? "Restart tilkoblingen" : "Start tilkobling"}
                            </Button>

                            {isTenantConnected && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    sx={{height: 40, width: 200}}
                                    onClick={handleDisconnectTenancy}
                                    disabled={entraIdLoading || excelLoading || disconnectLoading}
                                    startIcon={disconnectLoading ? <CircularProgress size={16} color="inherit"/> : null}
                                >
                                    Koble fra Entra ID
                                </Button>
                            )}
                        </Grid>
                    </Grid>

                    <EntraIdGraphicCard employeeImage={employeeImage} marginTop={"24px"} marginBottom={"12px"}/>
                    <EntraIdConflictingUsersCard marginTop={"24px"} marginBottom={"12px"}/>
                </Paper>

                <Paper sx={{m: 4, p: 3}}>
                    <Grid item xs={12}>
                        <Typography variant="h5" align="center" sx={{mb: 6}} className="container-header">
                            Excel Importering
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            Last ned malen, fyll den ut med ansattdata, og laste opp som excel fil. <br/>
                            Om du skal legge til flere ansatte må du bruke samme excel ark som inneholder alle ansatte du tidligere har importert.<br/>
                            <a href="https://docs.google.com/spreadsheets/d/1i_pZo55Mj6QJ0Ox7R8wgGhpSg0_QtNS11VBv72sYPLM/edit?usp=sharing"
                               target="_blank" rel="noopener noreferrer">
                                Mal for importering ansatte
                            </a>
                            <br/>
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sx={{mt: 3}}>
                        <Grid container justifyContent="space-between">
                            <Grid item xs={12} sm={12} md={6} sx={{mb: 2}}>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileChange}
                                    style={{display: 'none'}}
                                    accept=".xlsx"
                                />
                                <Button
                                    variant="outlined"
                                    className="SecondaryButton"
                                    sx={{height: 40, width: 200}}
                                    onClick={() => fileInputRef.current.click()}
                                >
                                    Velg Excel-filen '.xlsx'
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={12} md={6}>
                                <Button
                                    variant="contained"
                                    className="PrimaryButton"
                                    sx={{
                                        height: 40, width: 200,
                                        "&.Mui-disabled": {backgroundColor: "grey", color: "white"}
                                    }}
                                    onClick={handleFileUpload}
                                    disabled={!file || entraIdLoading || excelLoading || disconnectLoading}
                                    startIcon={excelLoading ? <CircularProgress size={16} color="inherit"/> : null}
                                >
                                    Last opp
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box className="modalBox">
                    <Typography id="modal-modal-title" variant="h6" color="text.secondary">
                        Vennligst kopiér koden og lim den inn på det tilsvarende stedet.
                    </Typography>
                    <Typography id="modal-modal-description" sx={{mt: 2}}>
                        {license}
                    </Typography>
                </Box>
            </Modal>
        </main>
    );
};

export default IntegrasjonPage;


// ─────────────────────────────────────────────────────────────────────────────
// Admin instruction dialog — shown after a successful disconnect (manual or
// triggered by an Excel upload). Backend can't remove the Enterprise Application
// from the customer's Entra tenant on their behalf, so we surface the exact
// steps the admin needs to take in Azure Portal. The message text comes from the
// backend so wording stays consistent across entry points.
// ─────────────────────────────────────────────────────────────────────────────
const AdminInstructionDialog = ({message, onClose}) => {
    const open = Boolean(message);
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{sx: {borderRadius: 2}}}
        >
            <DialogTitle sx={{display: "flex", alignItems: "center", gap: 1, pr: 6}}>
                <span style={{fontSize: "1.4em"}}>ℹ️</span>
                Ett siste steg gjenstår
                <IconButton
                    onClick={onClose}
                    sx={{position: "absolute", right: 8, top: 8, color: "grey.500"}}
                    aria-label="Lukk"
                >
                    ✕
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Alert severity="info" sx={{mb: 2}}>
                    <Typography variant="body1" sx={{whiteSpace: "pre-line"}}>
                        {message}
                    </Typography>
                </Alert>
                <Typography variant="body2" sx={{color: "text.secondary"}}>
                    Trenger dere hjelp med dette steget? Kontakt oss, så veileder vi dere gjennom det.
                </Typography>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} variant="contained" className="PrimaryButton">
                    Jeg har forstått
                </Button>
            </DialogActions>
        </Dialog>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Conflict report dialog — shown after Excel upload when one or more rows
// were rejected. Buckets:
//   • conflictedUsers — Excel rows whose identifiers (phone/email/ansattnummer)
//     collide with one or more existing users in a way the importer can't safely
//     resolve. Most common cause: an old Entra-sourced user has both a phone and
//     an email that the new Excel file splits across two different rows.
//   • alreadyClaimedUsers — Excel rows that match a user already belonging to a
//     different organization.
// ─────────────────────────────────────────────────────────────────────────────
const ConflictReportDialog = ({report, onClose}) => {
    const open = Boolean(report);
    const conflicted = report?.conflictedUsers ?? [];
    const claimed = report?.alreadyClaimedUsers ?? [];
    const invalid = report?.invalidRowMessages ?? [];

    const fullName = (u) => {
        const n = [u?.name, u?.surname].filter(Boolean).join(" ").trim();
        return n.length > 0 ? n : "(navn mangler)";
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{sx: {borderRadius: 2}}}
        >
            <DialogTitle sx={{display: "flex", alignItems: "center", gap: 1, pr: 6}}>
                <span style={{fontSize: "1.4em"}}>⚠️</span>
                Noen brukere kunne ikke importeres
                <IconButton
                    onClick={onClose}
                    sx={{position: "absolute", right: 8, top: 8, color: "grey.500"}}
                    aria-label="Lukk"
                >
                    ✕
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {conflicted.length > 0 && (
                    <Box sx={{mb: claimed.length > 0 ? 3 : 0}}>
                        <Alert severity="warning" sx={{mb: 2}}>
                            <strong>{conflicted.length}</strong>{" "}
                            {conflicted.length === 1 ? "rad" : "rader"} ble ikke importert fordi
                            identifikatorene (telefon, e-post eller ansattnummer) kommer i konflikt
                            med eksisterende brukere i databasen. Dette skjer typisk når en eksisterende
                            Entra ID-bruker dekker flere personer i Excel-filen, eller når én Excel-rad
                            treffer flere ulike brukere i databasen. Konflikten må løses manuelt.
                        </Alert>
                        <Typography variant="subtitle2" sx={{mb: 1, color: "text.secondary"}}>
                            Konflikter
                        </Typography>
                        <Stack spacing={1.25}>
                            {conflicted.map((u, i) => (
                                <UserCard key={`conflict-${i}`} user={u} fullName={fullName(u)}/>
                            ))}
                        </Stack>
                    </Box>
                )}

                {conflicted.length > 0 && claimed.length > 0 && <Divider sx={{my: 2}}/>}

                {claimed.length > 0 && (
                    <Box>
                        <Alert severity="info" sx={{mb: 2}}>
                            <strong>{claimed.length}</strong>{" "}
                            {claimed.length === 1 ? "bruker" : "brukere"} tilhører allerede en annen
                            organisasjon og kan derfor ikke importeres herfra.
                        </Alert>
                        <Typography variant="subtitle2" sx={{mb: 1, color: "text.secondary"}}>
                            Allerede tilknyttet annen organisasjon
                        </Typography>
                        <Stack spacing={1.25}>
                            {claimed.map((u, i) => (
                                <UserCard key={`claim-${i}`} user={u} fullName={fullName(u)}/>
                            ))}
                        </Stack>
                    </Box>
                )}

                {(conflicted.length > 0 || claimed.length > 0) && invalid.length > 0 && <Divider sx={{my: 2}}/>}

                {invalid.length > 0 && (
                    <Box>
                        <Alert severity="error" sx={{mb: 2}}>
                            <strong>{invalid.length}</strong>{" "}
                            {invalid.length === 1 ? "rad" : "rader"} ble hoppet over på grunn av ugyldig
                            data (f.eks. ugyldig telefonnummer eller manglende påkrevde felt). Resten av
                            filen ble importert. Korriger dataene og last opp på nytt for å inkludere disse.
                        </Alert>
                        <Typography variant="subtitle2" sx={{mb: 1, color: "text.secondary"}}>
                            Ugyldige rader
                        </Typography>
                        <Stack spacing={0.5}>
                            {invalid.map((msg, i) => (
                                <Typography key={i} variant="body2" sx={{color: "text.secondary", pl: 1}}>
                                    • {msg}
                                </Typography>
                            ))}
                        </Stack>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button onClick={onClose} variant="contained" className="PrimaryButton">
                    Lukk
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const UserCard = ({user, fullName}) => (
    <Paper
        variant="outlined"
        sx={{
            p: 1.75,
            borderRadius: 1.5,
            "&:hover": {borderColor: "primary.main"},
        }}
    >
        <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 0.75}}>
            {fullName}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{flexWrap: "wrap", gap: 0.75}}>
            {user?.phone && (
                <Chip size="small" variant="outlined" label={`📞 ${user.phone}`}/>
            )}
            {user?.email && (
                <Chip size="small" variant="outlined" label={`✉️ ${user.email}`}/>
            )}
            {user?.uniqueIdentifier && (
                <Chip size="small" variant="outlined" label={`🆔 ${user.uniqueIdentifier}`}/>
            )}
            {user?.departmentId && (
                <Chip size="small" variant="outlined" label={`🏢 ${user.departmentId}`}/>
            )}
            {user?.profession && (
                <Chip size="small" variant="outlined" label={user.profession}/>
            )}
        </Stack>
    </Paper>
);
